import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Palette, X } from "lucide-react";
import { SketchPicker } from "react-color";
import { useSelector } from "react-redux";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { SectionCard } from "../../../../components/design-system/admin/SectionCard";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { useSettingsApi } from "../../../../common/hooks/use-settings-api.hook";
import { VendorArPublicFiles } from "./vendor-ar-public-files";

type AnalyticsMode = "full" | "anonymous" | "off";

/**
 * The vendor's measurement mode. Configs written before the mode existed carry
 * only `analyticsEnabled`, and before that nothing at all — absent means on.
 * Mirrors resolveAnalyticsMode in pms-api-v2 (pms-events/analytics-mode.ts).
 */
export function vendorAnalyticsMode(config: any): AnalyticsMode {
    const mode = config?.analyticsMode;
    if (mode === "full" || mode === "anonymous" || mode === "off") return mode;
    return config?.analyticsEnabled === false ? "off" : "full";
}

// Labels and descriptions: ADMIN_VENDOREDIT.BRANDING.MODE_<VALUE> / MODE_<VALUE>_DESC.
const ANALYTICS_MODES: AnalyticsMode[] = ["full", "anonymous", "off"];

// Each language is named in itself — the way its speakers look for it.
const LANGUAGES: { value: string; name: string }[] = [
    { value: "fr", name: "Français" },
    { value: "en", name: "English" },
    { value: "es", name: "Español" },
    { value: "ar", name: "العربية" },
];

const QUALITIES = ["low", "medium", "high"] as const;
type Quality = (typeof QUALITIES)[number];

/**
 * The render quality as a three-stop scale, low on the left, high on the
 * right. The labels under the track are buttons too, so a stop can be picked
 * by its name as well as by dragging; the current one reads in the ink colour.
 */
export const QualitySlider = ({
    value,
    onChange,
    labelOf,
    labelledBy,
}: {
    value: Quality;
    onChange: (value: Quality) => void;
    labelOf: (value: Quality) => string;
    labelledBy?: string;
}) => {
    const index = Math.max(0, QUALITIES.indexOf(value));
    return (
        <div className="pt-2" data-testid="visualization-quality">
            <Slider
                value={[index]}
                min={0}
                max={QUALITIES.length - 1}
                step={1}
                aria-labelledby={labelledBy}
                getValueText={(i) => labelOf(QUALITIES[i])}
                onValueChange={([i]) => onChange(QUALITIES[i])}
            />
            <div className="mt-2.5 grid grid-cols-3 text-xs">
                {QUALITIES.map((q, i) => (
                    <button
                        key={q}
                        type="button"
                        onClick={() => onChange(q)}
                        aria-pressed={i === index}
                        data-testid={`visualization-quality-${q}`}
                        className={cn(
                            "transition-colors",
                            i === 0 ? "justify-self-start" : i === QUALITIES.length - 1 ? "justify-self-end" : "justify-self-center",
                            i === index ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {labelOf(q)}
                    </button>
                ))}
            </div>
        </div>
    );
};

// The vendor's brand colour when none is set yet. Vendor data, not UI chrome.
const DEFAULT_BRAND_COLOR = "#217ee8";

/** Black or white, whichever reads on the vendor's colour (a white brand colour hid a white icon). */
const inkOn = (hex: string): string => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
    if (!m) return "text-white";
    const n = parseInt(m[1], 16);
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? "text-black" : "text-white";
};

type ConfigField = "language" | "fontFamily" | "primaryColor" | "secondaryColor" | "visualizationQuality" | "analyticsEnabled";

const labelClass = "mb-1.5 text-xs font-medium text-muted-foreground";

export const VendorConfig = ({ vendor, setVendor, submitted }) => {
    const { t } = useTranslation();
    const [fonts, setFonts] = useState<any>([]);
    const { theme } = useSelector((state: VendorState) => state.settings);

    const [primaryColorOpen, setPrimaryColorOpen] = useState(false);
    const [secondaryColorOpen, setSecondaryColorOpen] = useState(false);

    const [tempPrimaryColor, setTempPrimaryColor] = useState(theme?.primaryColor);
    const [tempSecondaryColor, setTempSecondaryColor] = useState(theme?.secondaryColor);
    const settingsAPI = useSettingsApi();

    useEffect(() => {
        setFonts(settingsAPI.fonts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [touched, setTouched] = useState<Partial<Record<ConfigField, boolean>>>({});

    const handleChange = (event) => {
        const { name, value } = event.target;
        setTouched({ ...touched, [name]: true });
        setVendor({
            ...vendor,
            config: {
                ...vendor.config,
                [name]: name === "language" ? [value] : value,
            },
        });
    };

    // Written as a pair: the mode is what the API enforces, and the boolean is what
    // visualizers from before the mode existed still read.
    const setAnalyticsMode = (mode: AnalyticsMode) => {
        setTouched({ ...touched, analyticsEnabled: true });
        setVendor({
            ...vendor,
            config: {
                ...vendor.config,
                analyticsMode: mode,
                analyticsEnabled: mode !== "off",
            },
        });
    };
    const analyticsMode = vendorAnalyticsMode(vendor.config);

    const handlePrimaryColorChange = (color) => {
        setTempPrimaryColor(color.hex);
    };

    const handleSecondaryColorChange = (color) => {
        setTempSecondaryColor(color.hex);
    };

    const validatePrimaryColor = () => {
        setVendor({
            ...vendor,
            config: {
                ...vendor.config,
                primaryColor: tempPrimaryColor,
            },
        });
        setPrimaryColorOpen(false);
    };

    const validateSecondaryColor = () => {
        setVendor({
            ...vendor,
            config: {
                ...vendor.config,
                secondaryColor: tempSecondaryColor,
            },
        });
        setSecondaryColorOpen(false);
    };

    const colorField = (
        which: "primary" | "secondary",
        label: string,
        open: boolean,
        setOpen: (open: boolean) => void,
        temp: string,
        setTemp: (color: string) => void,
        onPick: (color: any) => void,
        onApply: () => void,
    ) => {
        const current = which === "primary" ? vendor.config?.primaryColor : vendor.config?.secondaryColor;
        return (
            <div className="sm:col-span-1 lg:col-span-2">
                <Label htmlFor={`vendor-config-${which}-color`} className={labelClass}>{label} *</Label>
                <Popover
                    open={open}
                    onOpenChange={(next) => {
                        if (next) setTemp(current || DEFAULT_BRAND_COLOR);
                        setOpen(next);
                    }}
                >
                    <PopoverTrigger asChild>
                        <button
                            id={`vendor-config-${which}-color`}
                            type="button"
                            aria-label={label}
                            data-testid={`vendor-${which}-color`}
                            className={cn(
                                "flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg font-mono text-xs uppercase ring-1 ring-foreground/10 transition-transform hover:scale-[1.02]",
                                inkOn(current || DEFAULT_BRAND_COLOR),
                            )}
                            // The vendor's configured brand colour is data, not chrome.
                            style={{ backgroundColor: current || DEFAULT_BRAND_COLOR }}
                        >
                            <Palette className="size-4" />
                            {current || DEFAULT_BRAND_COLOR}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-3">
                        <SketchPicker color={temp} onChange={onPick} />
                        <div className="mt-3 flex justify-end gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setTemp(current);
                                    setOpen(false);
                                }}
                            >
                                <X className="size-4" /> {t("ADMIN_SHELL.COMMON.CANCEL")}
                            </Button>
                            <Button size="sm" onClick={onApply}>
                                <Check className="size-4" /> {t("ADMIN_VENDOREDIT.BRANDING.APPLY")}
                            </Button>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        );
    };

    return (
        <SectionCard
            title={t("ADMIN_VENDOREDIT.BRANDING.TITLE")}
            description={t("ADMIN_VENDOREDIT.BRANDING.DESCRIPTION")}
            testId="vendor-branding-card"
        >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <Label htmlFor="vendor-config-language" className={labelClass}>{t("ADMIN_VENDOREDIT.BRANDING.LANGUAGE")} *</Label>
                    <Select
                        value={vendor.config?.language ? vendor.config?.language[0] : "fr"}
                        onValueChange={(value) => handleChange({ target: { name: "language", value } })}
                    >
                        <SelectTrigger id="vendor-config-language" className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {LANGUAGES.map((l) => (
                                <SelectItem key={l.value} value={l.value}>
                                    <span lang={l.value}>{l.name}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="lg:col-span-4">
                    <Label htmlFor="vendor-config-font-family" className={labelClass}>{t("ADMIN_VENDOREDIT.BRANDING.FONT_FAMILY")} *</Label>
                    <Select
                        value={(vendor?.config?.fontFamily ?? fonts[0]) || ""}
                        onValueChange={(value) => handleChange({ target: { name: "fontFamily", value } })}
                    >
                        <SelectTrigger id="vendor-config-font-family" className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {fonts?.map((font, index) => (
                                <SelectItem value={font} key={index}>
                                    {/* Preview each option in its own face — the font IS the data. */}
                                    <span style={{ fontFamily: font }}>{font}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {colorField(
                    "primary",
                    t("ADMIN_VENDOREDIT.BRANDING.PRIMARY_COLOR"),
                    primaryColorOpen,
                    setPrimaryColorOpen,
                    tempPrimaryColor,
                    setTempPrimaryColor,
                    handlePrimaryColorChange,
                    validatePrimaryColor,
                )}
                {colorField(
                    "secondary",
                    t("ADMIN_VENDOREDIT.BRANDING.SECONDARY_COLOR"),
                    secondaryColorOpen,
                    setSecondaryColorOpen,
                    tempSecondaryColor,
                    setTempSecondaryColor,
                    handleSecondaryColorChange,
                    validateSecondaryColor,
                )}

                {/* Three stops, low to high, rather than a list: the choice is a
                    scale, and its position says where the vendor sits on it. */}
                <div className="lg:col-span-4">
                    <Label id="vendor-config-visualization-quality-label" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.BRANDING.VISUALIZATION_QUALITY")}
                    </Label>
                    <QualitySlider
                        value={(vendor.config?.visualizationQuality ?? "medium") as Quality}
                        onChange={(value) => handleChange({ target: { name: "visualizationQuality", value } })}
                        labelOf={(q) => t(`ADMIN_VENDOREDIT.BRANDING.QUALITY_${q.toUpperCase()}`)}
                        labelledBy="vendor-config-visualization-quality-label"
                    />
                </div>

                {/* Measurement is a contractual position, which is why it lives here and
                    not on the vendor's own settings page: a vendor switching it back on
                    would silently invalidate the "nothing to consent to" classification
                    their privacy team signed off on. Absent means on — every config
                    written before this field existed has no value for it. */}
                <div className="flex flex-col gap-2.5 rounded-lg border p-4 sm:col-span-2 lg:col-span-8">
                    <span id="analytics-mode-label" className="text-sm font-medium">
                        {t("ADMIN_VENDOREDIT.BRANDING.ANALYTICS_TITLE")}
                    </span>
                    <ToggleGroup
                        type="single"
                        size="sm"
                        variant="outline"
                        value={analyticsMode}
                        onValueChange={(value) => value && setAnalyticsMode(value as AnalyticsMode)}
                        aria-labelledby="analytics-mode-label"
                        className="self-start"
                    >
                        {ANALYTICS_MODES.map((mode) => (
                            <ToggleGroupItem
                                key={mode}
                                value={mode}
                                // The toggle marks "on" with bg-muted, which can match the ground
                                // it sits on, so the choice vanished. Selected is an ink outline
                                // (inset: an outer ring is clipped by overflow-hidden parents).
                                className="bg-background px-3 text-xs text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:ring-[1.5px] data-[state=on]:ring-inset data-[state=on]:ring-foreground"
                            >
                                {t(`ADMIN_VENDOREDIT.BRANDING.MODE_${mode.toUpperCase()}`)}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                    <span className="text-xs text-muted-foreground">
                        {t(`ADMIN_VENDOREDIT.BRANDING.MODE_${analyticsMode.toUpperCase()}_DESC`)}
                    </span>
                </div>

                {/* "See it realistically" for furniture: an AI render with a real cost per
                    image, so Ardealy decides per vendor rather than the vendor itself.
                    Absent means on - configs written before this field have no value. */}
                <div className="flex items-start justify-between gap-4 rounded-lg border p-4 sm:col-span-2 lg:col-span-8">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="vendor-config-furniture-render" className="text-sm font-medium">
                            {t("ADMIN_VENDOREDIT.BRANDING.FURNITURE_RENDER_TITLE")}
                        </label>
                        <span className="text-xs text-muted-foreground">
                            {t("ADMIN_VENDOREDIT.BRANDING.FURNITURE_RENDER_DESC")}
                        </span>
                    </div>
                    <Switch
                        id="vendor-config-furniture-render"
                        checked={vendor.config?.furnitureRenderEnabled !== false}
                        onCheckedChange={(checked) =>
                            setVendor({ ...vendor, config: { ...vendor.config, furnitureRenderEnabled: checked } })}
                    />
                </div>

                {/* Surface finishes (reflections): a finish changes how a product looks,
                    so Ardealy turns it on per vendor. Absent means off. */}
                <div className="flex items-start justify-between gap-4 rounded-lg border p-4 sm:col-span-2 lg:col-span-8">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="vendor-config-finishes" className="text-sm font-medium">
                            {t("ADMIN_VENDOREDIT.BRANDING.FINISHES_TITLE")}
                        </label>
                        <span className="text-xs text-muted-foreground">
                            {t("ADMIN_VENDOREDIT.BRANDING.FINISHES_DESC")}
                        </span>
                    </div>
                    <Switch
                        id="vendor-config-finishes"
                        checked={vendor.config?.finishesEnabled === true}
                        onCheckedChange={(checked) =>
                            setVendor({ ...vendor, config: { ...vendor.config, finishesEnabled: checked } })}
                    />
                </div>

                {/* Native AR files: public copies of encrypted models, Ardealy's call per
                    vendor. Applied at once through its own route; an existing vendor only. */}
                {(vendor.uuid || vendor._id) && (
                    <VendorArPublicFiles vendorId={vendor.uuid || vendor._id} config={vendor.config} />
                )}
            </div>
        </SectionCard>
    );
};
